import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (polls.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      polls.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6 pt-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-md w-full border border-gold/25 bg-ink-surface p-10 text-center" data-testid="payment-success">
        {state === "checking" && (
          <>
            <Loader2 size={34} className="text-gold mx-auto animate-spin" />
            <h1 className="font-serif text-3xl mt-6">Confirming Payment</h1>
            <p className="font-sans text-sm text-[#A19D98] mt-3">Anchoring your order to the registry…</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={40} className="text-gold mx-auto" strokeWidth={1.4} />
            <h1 className="font-serif text-3xl mt-6">Order Confirmed</h1>
            <p className="font-sans text-sm text-[#A19D98] mt-3">Your physical Membership ID is now in production and will be mailed to your address. Your digital ID is available in the Vault.</p>
            <Link to="/vault" data-testid="success-vault-link" className="group inline-flex items-center gap-3 mt-8 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
              Return to The Vault <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle size={40} className="text-[#c07a7a] mx-auto" strokeWidth={1.4} />
            <h1 className="font-serif text-3xl mt-6">{state === "timeout" ? "Still Processing" : "Something Went Wrong"}</h1>
            <p className="font-sans text-sm text-[#A19D98] mt-3">{state === "timeout" ? "Your payment is taking longer than usual to confirm. Check your orders in the Vault shortly." : "We could not confirm this payment. If you were charged, it will appear in your Vault orders."}</p>
            <Link to="/vault" className="group inline-flex items-center gap-3 mt-8 px-8 py-4 border border-gold/40 text-gold font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold/5 transition-colors">
              Go to The Vault <ArrowRight size={14} />
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6 pt-24">
      <div className="max-w-md w-full border border-gold/20 bg-ink-surface p-10 text-center" data-testid="payment-cancel">
        <XCircle size={40} className="text-[#A19D98] mx-auto" strokeWidth={1.4} />
        <h1 className="font-serif text-3xl mt-6">Order Cancelled</h1>
        <p className="font-sans text-sm text-[#A19D98] mt-3">No charge was made. You can order your physical Membership ID any time from the Vault.</p>
        <Link to="/vault" className="group inline-flex items-center gap-3 mt-8 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
          Return to The Vault <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
